/**
 * MediaRecorder-based voice recording hook.
 * Does NOT use Web Speech API (unreliable on iOS Safari).
 * Caps recording at MAX_SECONDS (default 15).
 */
import { useState, useRef, useCallback, useEffect } from "react";

const MAX_SECONDS = 15;

export function useVoiceRecorder() {
  const [state, setState] = useState("idle"); // idle | recording | error
  const [secondsLeft, setSecondsLeft] = useState(MAX_SECONDS);
  const [permissionDenied, setPermissionDenied] = useState(false);

  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const countRef = useRef(null);

  const stop = useCallback(() => {
    if (mediaRef.current && mediaRef.current.state !== "inactive") {
      mediaRef.current.stop();
    }
    clearTimeout(timerRef.current);
    clearInterval(countRef.current);
  }, []);

  const start = useCallback(async (onBlob) => {
    if (state === "recording") return;

    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch {
      setPermissionDenied(true);
      setState("error");
      return;
    }

    chunksRef.current = [];
    setSecondsLeft(MAX_SECONDS);
    setState("recording");

    const mimeType = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
      ? "audio/webm;codecs=opus"
      : MediaRecorder.isTypeSupported("audio/webm")
      ? "audio/webm"
      : "";

    const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : {});
    mediaRef.current = recorder;

    recorder.ondataavailable = (e) => {
      if (e.data.size > 0) chunksRef.current.push(e.data);
    };

    recorder.onstop = () => {
      stream.getTracks().forEach((t) => t.stop());
      const blob = new Blob(chunksRef.current, {
        type: mimeType || "audio/webm",
      });
      setState("idle");
      setSecondsLeft(MAX_SECONDS);
      onBlob(blob);
    };

    recorder.start(250);

    // Auto-stop after MAX_SECONDS
    timerRef.current = setTimeout(() => {
      if (recorder.state !== "inactive") recorder.stop();
    }, MAX_SECONDS * 1000);

    countRef.current = setInterval(() => {
      setSecondsLeft((s) => {
        if (s <= 1) {
          clearInterval(countRef.current);
          return 0;
        }
        return s - 1;
      });
    }, 1000);
  }, [state]);

  useEffect(() => () => stop(), [stop]);

  return { state, secondsLeft, permissionDenied, start, stop };
}
